/** Build an iCalendar file for an open house (floating local time). */

interface OpenHouseEvent {
  address: string;
  city: string;
  state: string;
  zip: string;
  /** YYYY-MM-DD (or RFC3339 — only the date part is used) */
  date: string;
  /** e.g. "13:00" or "1:00 PM" */
  startTime: string;
  endTime: string;
  url?: string;
}

function toIcsTime(date: string, time: string): string {
  const day = date.slice(0, 10).replace(/-/g, '');
  let hours = 0;
  let minutes = 0;

  const ampm = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  const h24 = time.match(/^(\d{1,2}):(\d{2})$/);
  if (ampm) {
    hours = parseInt(ampm[1], 10) % 12;
    if (ampm[3].toUpperCase() === 'PM') hours += 12;
    minutes = parseInt(ampm[2], 10);
  } else if (h24) {
    hours = parseInt(h24[1], 10);
    minutes = parseInt(h24[2], 10);
  }

  return `${day}T${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}00`;
}

function escapeText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

function eventLines(event: OpenHouseEvent): string[] {
  const location = `${event.address}, ${event.city}, ${event.state} ${event.zip}`;
  const uid = `openhouse-${event.date.slice(0, 10)}-${event.address.replace(/\W+/g, '-').toLowerCase()}@goreal`;
  return [
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTART:${toIcsTime(event.date, event.startTime)}`,
    `DTEND:${toIcsTime(event.date, event.endTime)}`,
    `SUMMARY:${escapeText(`Open House — ${event.address}`)}`,
    `LOCATION:${escapeText(location)}`,
    `DESCRIPTION:${escapeText(
      `Open house at ${location}. No appointment needed.${event.url ? ` Listing: ${event.url}` : ''}`
    )}`,
    'END:VEVENT',
  ];
}

function wrapCalendar(events: string[]): string {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GoReal//Open House//EN',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n');
}

export function buildOpenHouseIcs(event: OpenHouseEvent): string {
  return wrapCalendar(eventLines(event));
}

/** One calendar file bundling several open houses (e.g. a whole day's tour). */
export function buildMultiIcs(events: OpenHouseEvent[]): string {
  return wrapCalendar(events.flatMap(eventLines));
}

export function downloadIcs(filename: string, ics: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
